// Chronological log of marketing-email opens, sourced from Brevo's own
// delivery-tracking webhook (the same one that already powers subscriber
// engagement stats) - shows who opened what and when, useful for spotting
// who's actually engaging (e.g. matching an open to when feedback or a
// review came in).
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Select, Pill } from '../lib/ui';

const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: 'var(--muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13, color: 'var(--text)' };

const TYPE_LABEL = {
  feedback:           'Feedback invite',
  referral:           'Referral code',
  campaign:           'Campaign',
  outreach:           'Outreach',
  transactional:      'Order/receipt',
  admin_notification: 'Admin notification',
  other:              'Other',
};
const TYPE_TONE = {
  feedback:           'blue',
  referral:           'ok',
  campaign:           'ok',
  outreach:           'blue',
  transactional:      'ok',
  admin_notification: 'warn',
  other:              'warn',
};

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function EmailActivityTab() {
  const [events, setEvents] = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/email/opens?limit=300');
      setEvents(r.data.events || []);
      setTotal(r.data.total || 0);
    } catch {
      setErr('Failed to load email activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = events
    .filter(e => !typeFilter || e.emailType === typeFilter)
    .filter(e => !emailFilter || (e.email || '').toLowerCase().includes(emailFilter.trim().toLowerCase()));

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, maxWidth: 700 }}>
        Opens are logged from Brevo's delivery tracking (the same source behind subscriber
        engagement stats) — who opened what, and when. Use it to spot who's actually engaging
        (e.g. matching an open to when feedback or a review came in).
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: 200 }}>
          <option value="">All email types</option>
          <option value="feedback">Feedback invite</option>
          <option value="campaign">Campaign</option>
          <option value="outreach">Outreach</option>
          <option value="transactional">Order/receipt</option>
          <option value="admin_notification">Admin notification</option>
        </Select>
        <Input placeholder="Search by email…" value={emailFilter} onChange={e => setEmailFilter(e.target.value)} style={{ width: 220 }} />
        <Button onClick={load} style={{ fontSize: 12 }}>↻ Refresh</Button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)' }}>
          {rows.length} shown{(typeFilter || emailFilter) ? ` of ${total} total` : ''}
        </span>
      </div>

      {err && <div style={{ padding: 10, background: '#fff5f5', border: '1px solid #f5c6cb', marginBottom: 12, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#b00020' }}>{err}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={th}>Opened</th>
              <th style={th}>Opened by</th>
              <th style={th}>Type</th>
              <th style={th}>Subject / label</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No opens logged yet.</td></tr>
            )}
            {!loading && rows.map(ev => (
              <tr key={ev._id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={td}>{formatDateTime(ev.openedAt)}</td>
                <td style={{ ...td, fontWeight: 600 }}>{ev.email || <span style={{ color: 'var(--muted)', fontWeight: 400 }}>unknown</span>}</td>
                <td style={td}>
                  <Pill tone={TYPE_TONE[ev.emailType] || 'blue'}>{TYPE_LABEL[ev.emailType] || ev.emailType}</Pill>
                </td>
                <td style={{ ...td, color: ev.label ? 'var(--text)' : 'var(--muted)' }}>{ev.label || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
